import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { formatDateTimeDMY } from '../utils/dateUtils';
import { Bell, Check, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Notifications() {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      fetchNotifications();
      toast.success(t('savedSuccess'));
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'contract_expiry':
        return <div className="p-2 bg-amber-100 text-amber-700"><Bell size={20} weight="fill" /></div>;
      case 'inspection':
        return <div className="p-2 bg-blue-100 text-blue-700"><Bell size={20} weight="fill" /></div>;
      case 'payment_due':
        return <div className="p-2 bg-red-100 text-red-700"><Bell size={20} weight="fill" /></div>;
      default:
        return <div className="p-2 bg-zinc-100 text-zinc-700"><Bell size={20} weight="fill" /></div>;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('notifications')}
            </h2>
            {unreadCount > 0 && (
              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold">
                {unreadCount}
              </span>
            )}
          </div>
          {notifications.length > 0 && (
            <Button
              variant="outline"
              onClick={markAllRead}
              className="border-zinc-200 rounded-none"
              data-testid="mark-all-read-btn"
            >
              <CheckCircle size={18} className="mr-2" />
              {t('markAllRead')}
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="bg-white border border-zinc-200">
          {notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell size={48} className="mx-auto text-zinc-300 mb-4" />
              <p className="text-zinc-500">{t('noNotifications')}</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  className={`p-4 flex gap-4 ${notification.is_read ? 'bg-white' : 'bg-zinc-50'}`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  {getNotificationIcon(notification.type)}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium ${notification.is_read ? 'text-zinc-600' : 'text-zinc-900'}`}>
                      {notification.title}
                    </h3>
                    <p className="text-sm text-zinc-500 mt-1">{notification.message}</p>
                    <p className="text-xs text-zinc-400 mt-2 font-mono">
                      {formatDateTimeDMY(notification.created_at)}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <button
                      onClick={() => markAsRead(notification.notification_id)}
                      className="p-2 hover:bg-zinc-100 transition-colors self-start"
                      data-testid={`mark-read-${notification.notification_id}`}
                    >
                      <Check size={18} className="text-zinc-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
